import React, { useMemo } from "react";
import { Badge, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import CustomToggleButton from "../customToggleButton/customToggleButton";
import {
  HIKE_SCOPE,
  HIKE_TYPE,
  applyHikeToUnitPrice,
  tableMatchesHikeScope,
} from "../../helpers/tableBookingPriceHike";
import {
  flattenLayoutPricingRows,
  indexAreaPricingRows,
  resolveEffectiveEventPricing,
} from "../../helpers/eventTableBooking";
import {
  liveStatusKeyFromSheetTable,
  resolveLiveSheetTable,
} from "../../helpers/eventTableBookingLiveStatus";
import { formatInr } from "../../helpers/ticketSalePhases";
import "../../pages/eventPricingHub/eventPricingHub.css";

const TablePriceHikePanel = ({
  hike,
  onChange,
  layout,
  layoutLoading,
  liveIndex,
  areaPricing,
  tableBookingEnabled,
}) => {
  const { t } = useTranslation();
  const rows = useMemo(() => flattenLayoutPricingRows(layout), [layout]);
  const tableRows = useMemo(
    () => rows.filter((r) => r.kind === "table"),
    [rows]
  );
  const zoneRows = useMemo(() => rows.filter((r) => r.kind === "zone"), [rows]);
  const overrideIndex = useMemo(
    () => indexAreaPricingRows(areaPricing),
    [areaPricing]
  );

  const setHike = (patch) => onChange({ ...hike, ...patch });

  const preview = useMemo(() => {
    return tableRows.map((row) => {
      const live = resolveLiveSheetTable(row, liveIndex);
      const liveKey = liveStatusKeyFromSheetTable(live);
      const booked = liveKey && liveKey !== "available";
      const effective = resolveEffectiveEventPricing(row, overrideIndex);
      const base = Number(effective?.price) || 0;
      const inScope = tableMatchesHikeScope(row.areaId, row.tableId, hike);
      const hiked = inScope && hike.enabled ? applyHikeToUnitPrice(base, hike) : base;
      return { row, booked, liveKey, base, hiked, inScope };
    });
  }, [tableRows, liveIndex, overrideIndex, hike]);

  const remainingInScope = preview.filter((p) => p.inScope && !p.booked).length;

  if (!tableBookingEnabled) {
    return (
      <div className="eph-empty">
        <i className="bx bx-grid-alt" />
        <strong>{t("EVENT_HIKE_NEED_TABLES_TITLE")}</strong>
        <span>{t("EVENT_HIKE_NEED_TABLES")}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="eph-tier-head">
        <div>
          <div className="fw-semibold">{t("EVENT_HIKE_TOGGLE")}</div>
          <div className="eph-tier-meta">{t("EVENT_HIKE_TOGGLE_HINT")}</div>
        </div>
        <CustomToggleButton
          id="table-price-hike"
          checked={Boolean(hike?.enabled)}
          onCheckChange={(checked) => setHike({ enabled: checked })}
        />
      </div>

      {!hike?.enabled ? (
        <p className="eph-tier-meta mt-3 mb-0">{t("EVENT_HIKE_OFF_HINT")}</p>
      ) : (
        <>
          <div className="hike-scope" role="group" aria-label={t("EVENT_HIKE_SCOPE")}>
            {[
              { id: HIKE_SCOPE.ALL, label: t("EVENT_HIKE_SCOPE_ALL") },
              { id: HIKE_SCOPE.SECTION, label: t("EVENT_HIKE_SCOPE_SECTION") },
              { id: HIKE_SCOPE.TABLE, label: t("EVENT_HIKE_SCOPE_TABLE") },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={hike.scope === opt.id ? "is-on" : ""}
                onClick={() => setHike({ scope: opt.id })}
              >
                {opt.label}
              </button>
            ))}
          </div>

      <div className="eph-hike-fields">
            <div>
              <Form.Label>{t("EVENT_HIKE_TYPE")}</Form.Label>
              <Form.Select
                value={hike.hikeType}
                onChange={(e) => setHike({ hikeType: e.target.value })}
              >
                <option value={HIKE_TYPE.PERCENT}>
                  {t("EVENT_HIKE_TYPE_PERCENT")}
                </option>
                <option value={HIKE_TYPE.FIXED}>
                  {t("EVENT_HIKE_TYPE_FIXED")}
                </option>
              </Form.Select>
            </div>
            <div>
              <Form.Label>{t("EVENT_HIKE_VALUE")}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                inputMode="decimal"
                value={hike.hikeValue ?? ""}
                onChange={(e) => setHike({ hikeValue: e.target.value })}
              />
            </div>
            <div className="eph-remaining-chip">
              {t("EVENT_HIKE_REMAINING_COUNT", { n: remainingInScope })}
            </div>
          </div>

          {layoutLoading ? (
            <Spinner animation="border" size="sm" />
          ) : !layout ? (
            <p className="eph-tier-meta">{t("EVENT_HIKE_NO_LAYOUT")}</p>
          ) : (
            <>
              {hike.scope === HIKE_SCOPE.SECTION ? (
                <div className="eph-zone-pills">
                  {zoneRows.map((zone) => {
                    const checked = (hike.areaIds || []).includes(zone.areaId);
                    return (
                      <button
                        key={zone.areaId}
                        type="button"
                        className={`eph-chip${checked ? " is-on" : ""}`}
                        onClick={() => {
                          const next = new Set(hike.areaIds || []);
                          if (checked) next.delete(zone.areaId);
                          else next.add(zone.areaId);
                          setHike({ areaIds: [...next] });
                        }}
                      >
                        {zone.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="table-responsive">
                <Table className="hike-preview-table" size="sm" hover>
                  <thead>
                    <tr>
                      {hike.scope === HIKE_SCOPE.TABLE ? (
                        <th>{t("EVENT_HIKE_SELECT")}</th>
                      ) : null}
                      <th>{t("EVENT_HIKE_COL_SECTION")}</th>
                      <th>{t("EVENT_HIKE_COL_TABLE")}</th>
                      <th>{t("EVENT_HIKE_COL_STATUS")}</th>
                      <th>{t("EVENT_HIKE_COL_PRICE")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map(({ row, booked, base, hiked, inScope }) => {
                      const selected = (hike.tables || []).some(
                        (x) =>
                          x.areaId === row.areaId && x.tableId === row.tableId
                      );
                      return (
                        <tr key={`${row.areaId}:${row.tableId}`}>
                          {hike.scope === HIKE_SCOPE.TABLE ? (
                            <td data-label={t("EVENT_HIKE_SELECT")}>
                              <Form.Check
                                type="checkbox"
                                disabled={booked}
                                checked={selected}
                                onChange={(e) => {
                                  const rest = (hike.tables || []).filter(
                                    (x) =>
                                      !(
                                        x.areaId === row.areaId &&
                                        x.tableId === row.tableId
                                      )
                                  );
                                  setHike({
                                    tables: e.target.checked
                                      ? [
                                          ...rest,
                                          {
                                            areaId: row.areaId,
                                            tableId: row.tableId,
                                          },
                                        ]
                                      : rest,
                                  });
                                }}
                              />
                            </td>
                          ) : null}
                          <td data-label={t("EVENT_HIKE_COL_SECTION")}>
                            {row.zoneLabel || "—"}
                          </td>
                          <td data-label={t("EVENT_HIKE_COL_TABLE")}>
                            {row.label}
                          </td>
                          <td data-label={t("EVENT_HIKE_COL_STATUS")}>
                            <Badge
                              className={booked ? "badge-sold" : "badge-open"}
                            >
                              {booked
                                ? t("EVENT_HIKE_SOLD")
                                : t("EVENT_HIKE_AVAILABLE")}
                            </Badge>
                          </td>
                          <td data-label={t("EVENT_HIKE_COL_PRICE")}>
                            {inScope && hike.enabled && hiked !== base ? (
                              <>
                                <span className="price-strike">
                                  {formatInr(base)}
                                </span>
                                <span className="price-new">
                                  {formatInr(hiked)}
                                </span>
                              </>
                            ) : (
                              formatInr(base)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TablePriceHikePanel;
